import { client } from '../../utils/axios-utils';

const url = `${process.env.REACT_APP_CORPORATECHAT_API_URL}/${process.env.REACT_APP_API_URI}/messagegroups`;

const appendMessagesAndFiles = (formData, messages, files) => {
	(messages || []).forEach((message, index) => {
		formData.append(`Messages[${index}].MessageText`, message.messageText || '');
		formData.append(`Messages[${index}].ExternalReference`, message.externalReference);
		formData.append(`Messages[${index}].Order`, String(index));
	});

	(files || []).forEach(file => {
		formData.append('Files', file);
	});
}

/** A criação é multipart/form-data, com os arrays em notação indexada, que é como o
 *  model binder do .NET lê. Valem os mesmos cuidados do envio de mensagens (ver
 *  messages.js): sem `Content-Type` explícito, para o browser gerar o boundary.
 *
 *  - Quem só visualiza vai sem nenhuma chave `Permissions` (View é implícita).
 *  - O `Order` é reindexado aqui: se o usuário apagou uma mensagem do meio da fila,
 *    não pode haver ordem repetida.
 *  - Sem mensagens, nenhuma chave `Messages` é enviada e o grupo nasce vazio.
 */
const buildCreateGroupFormData = ({ name, participants, messages, files }) => {
	const formData = new FormData();

	formData.append('Name', (name || '').trim());

	(participants || []).forEach((participant, index) => {
		formData.append(`Participants[${index}].UserId`, String(participant.userId));
		(participant.permissions || []).forEach((permission, permissionIndex) => {
			formData.append(`Participants[${index}].Permissions[${permissionIndex}]`, String(permission));
		});
	});

	appendMessagesAndFiles(formData, messages, files);

	return formData;
}

/** O envio dentro do grupo segue o mesmo formato, com uma mensagem só. Mensagem só
 *  com anexo vai com o texto vazio: a API exige ao menos uma mensagem ou um anexo.
 */
const buildGroupMessageFormData = ({ messageText, externalReference, files }) => {
	const formData = new FormData();

	appendMessagesAndFiles(formData, [{ messageText, externalReference }], files);

	return formData;
}

const messageGroupsService = {
	postMessageGroups: (params) => {
		return new Promise((resolve, reject) => {
			return client()
				.post(url, buildCreateGroupFormData(params))
				.then((res) => resolve(res.data))
				.catch((error) => reject(error));
		});
	},
	getMessageGroup: (groupId) => {
		return new Promise((resolve, reject) => {
			return client()
				.get(`${url}/${groupId}`)
				.then((res) => resolve(res.data))
				.catch((error) => reject(error));
		});
	},
	/** `lastId` é o id da mensagem mais antiga já carregada (0 na primeira página) e
	 *  `orderBy` vai em maiúsculas ("DESC"), como está na documentação da API.
	 */
	getMessageGroupMessages: (groupId, params) => {
		return new Promise((resolve, reject) => {
			return client()
				.get(`${url}/${groupId}/messages`, { params })
				.then((res) => resolve(res.data))
				.catch((error) => reject(error));
		});
	},
	postMessageGroupMessages: (groupId, params) => {
		return new Promise((resolve, reject) => {
			return client()
				.post(`${url}/${groupId}/messages`, buildGroupMessageFormData(params))
				.then((res) => resolve(res.data))
				.catch((error) => reject(error));
		});
	},
	/** Adiciona participantes e, para quem já está no grupo, SUBSTITUI as permissões
	 *  pelas enviadas: é também a rota de trocar o nível. Quem só visualiza vai com
	 *  `permissions: []`.
	 */
	postMessageGroupParticipants: (groupId, participants) => {
		return new Promise((resolve, reject) => {
			return client()
				.post(`${url}/${groupId}/participants`, { participants })
				.then((res) => resolve(res.data))
				.catch((error) => reject(error));
		});
	},
	/** Remover a si mesmo é sair do grupo. */
	deleteMessageGroupParticipant: (groupId, userId) => {
		return new Promise((resolve, reject) => {
			return client()
				.delete(`${url}/${groupId}/participants/${userId}`)
				.then((res) => resolve(res.data))
				.catch((error) => reject(error));
		});
	},
	putMessageGroup: (groupId, { name }) => {
		return new Promise((resolve, reject) => {
			return client()
				.put(`${url}/${groupId}`, { name })
				.then((res) => resolve(res.data))
				.catch((error) => reject(error));
		});
	},
	/** Exclusão lógica: o grupo fica inativo e o histórico continua legível pela API. */
	deleteMessageGroup: (groupId) => {
		return new Promise((resolve, reject) => {
			return client()
				.delete(`${url}/${groupId}`)
				.then((res) => resolve(res.data))
				.catch((error) => reject(error));
		});
	}
};

export { buildCreateGroupFormData, buildGroupMessageFormData };

export default messageGroupsService;
